import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { adminSupabase as supabase } from '@/integrations/supabase/adminClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Loader2, CheckCircle, Copy } from 'lucide-react';
import { BrandedSpinner } from '@/components/ui/branded-spinner';
import { toast } from 'sonner';
import paynovusLogo from '@/assets/paynovus-logo-horizontal-tagline.png';

export default function AdminMFASetup() {
  const { user, adminUser, adminUserLoaded, loading } = useAdminAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<'loading' | 'enroll' | 'verify'>('loading');
  const [factorId, setFactorId] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    if (loading || !adminUserLoaded) return;
    // Only block if there's no authenticated user at all (not logged in)
    if (!user) {
      navigate('/admin/login');
      return;
    }
    // If adminUser is null but user exists, they still need MFA setup
    // (adminUser loading failure shouldn't block MFA enrollment)
    checkMFAStatus();
  }, [loading, adminUserLoaded, user]);

  async function checkMFAStatus() {
    const { data } = await supabase.auth.mfa.listFactors();
    const verifiedFactor = data?.totp?.find(f => f.factor_type === 'totp' && f.status === 'verified');
    if (verifiedFactor) {
      navigate('/admin');
      return;
    }

    // Check for unverified factors and clean them up
    const unverified = data?.totp?.filter(f => f.status !== 'verified') || [];
    for (const f of unverified) {
      await supabase.auth.mfa.unenroll({ factorId: f.id });
    }

    // Start enrollment
    enrollMFA();
  }

  async function enrollMFA() {
    const { data, error: enrollErr } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: `PayNovus Admin TOTP ${Date.now()}`,
    });

    if (enrollErr) {
      setError(enrollErr.message);
      setStep('enroll');
      return;
    }

    if (data) {
      setFactorId(data.id);
      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
      setStep('enroll');
    }
  }

  async function handleVerify() {
    setError(null);
    setIsVerifying(true);

    try {
      const { data: challenge, error: challengeErr } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeErr) {
        setError(challengeErr.message);
        return;
      }

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code: verifyCode,
      });

      if (verifyErr) {
        setError('Invalid code. Please try again.');
        return;
      }

      // Mark MFA as enabled in admin_users
      await supabase
        .from('admin_users')
        .update({
          mfa_enabled: true,
          mfa_enrolled_at: new Date().toISOString(),
          mfa_method: 'totp',
        })
        .eq('user_id', user!.id);

      toast.success('MFA enrolled successfully');
      navigate('/admin');
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  }

  if (loading || !adminUserLoaded || step === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <BrandedSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img src={paynovusLogo} alt="PayNovus" className="h-12" />
          </div>
          <div className="flex items-center justify-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            <CardTitle className="text-2xl">MFA Enrollment Required</CardTitle>
          </div>
          <CardDescription>
            Multi-factor authentication is mandatory for admin access. 
            Set up your authenticator app to continue.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {step === 'enroll' && qrCode && (
            <>
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  <strong>Step 1:</strong> Scan this QR code with your authenticator app 
                  (Google Authenticator, Authy, 1Password, etc.)
                </div>
                
                <div className="flex justify-center p-4 bg-white rounded-lg">
                  <img src={qrCode} alt="MFA QR Code" className="w-48 h-48" />
                </div>

                <div className="text-sm text-muted-foreground">
                  <strong>Or enter this secret manually:</strong>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-muted rounded text-xs break-all font-mono">
                    {secret}
                  </code>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(secret);
                      toast.info('Secret copied');
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  <strong>Step 2:</strong> Enter the 6-digit code from your authenticator app
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Verification Code</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder=""
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-2xl tracking-widest"
                    disabled={isVerifying}
                  />
                </div>
                <Button
                  className="w-full"
                  onClick={handleVerify}
                  disabled={verifyCode.length !== 6 || isVerifying}
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Verify & Enable MFA
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
